import { useCallback, useEffect, useMemo, useState } from 'react';
import { MRT_Row } from 'material-react-table';
import { openToast, openToastError } from 'src/use-cases/toast/useToast';
import { createRequiredValidator, useTableValidation } from './useTableValidation';
import { useEnhancedTable } from './useEnhancedTable';

export interface UseSettingsTableConfig<TEntity, TFilters> {
  // Required configurations
  entityName: string;
  requiredFields: readonly (keyof TEntity)[];

  // API hooks
  useEntityList: (params: any) => {
    data: { data?: { content?: TEntity[]; totalElements?: number } };
    isLoading: boolean;
    isError: boolean;
    refetch: () => void;
  };
  useEntityMetadata: (filters: TFilters) => {
    data: { data?: any };
    refetch: () => void;
  };
  useUpsertEntity: () => {
    mutateAsync: (entity: TEntity) => Promise<any>;
  };

  // Configuration
  searchQuery?: string;
  initialSorting?: Array<{ id: string; desc: boolean }>;

  // Custom handlers
  prepareRequestBody?: (filters: TFilters, pagination: any, sorting: any) => any;
  prepareEntityBody?: (values: Record<string, any>, original?: TEntity) => TEntity;
  customValidation?: (entity: TEntity) => boolean;
}

export interface UseSettingsTableReturn<TEntity, TFilters> {
  // State
  selectedEntityId: number | string | null;
  setSelectedEntityId: (id: number | string | null) => void;
  filters: TFilters;
  setFilters: (filters: TFilters | ((prev: TFilters) => TFilters)) => void;

  // Data
  entities: TEntity[];
  entitiesTotal: number;
  isLoading: boolean;
  isError: boolean;

  // Pagination & Sorting
  pagination: any;
  sorting: any;
  handleSortingChange: (sorting: any) => void;
  onChangePage: (page: number) => void;
  onChangePageSize: (size: number) => void;

  // Table operations
  handleEntitySave: (params: {
    values: Record<string, any>;
    row?: MRT_Row<TEntity>;
    exitEditingMode?: () => void;
    exitCreatingMode?: () => void;
  }) => Promise<void>;

  // Validation
  getTextFieldProps: any;
  validateItem: (entity: TEntity) => boolean;
  clearValidationErrors: () => void;

  // Filtering
  updateFilter: <K extends keyof TFilters>(key: K, value: TFilters[K]) => void;
  handleTableSort: (columnId: string, direction: 'asc' | 'desc') => void;
  handleTableFilter: (columnId: string, selectedValues: string[], allOptions?: { id: string; label: string; count: number }[]) => void;

  // Metadata
  metadataData: any;
  refetchMetadata: () => void;
  refetch: () => void;
}

export function useSettingsTable<TEntity extends { id?: number | string }, TFilters extends Record<string, any>>(
  config: UseSettingsTableConfig<TEntity, TFilters>
): UseSettingsTableReturn<TEntity, TFilters> {
  const {
    entityName,
    requiredFields,
    useEntityList,
    useEntityMetadata,
    useUpsertEntity,
    searchQuery,
    initialSorting = [{ id: 'id', desc: false }],
    prepareRequestBody,
    prepareEntityBody,
    customValidation,
  } = config;

  // State management
  const [selectedEntityId, setSelectedEntityId] = useState<number | string | null>(null);
  const [filters, setFilters] = useState<TFilters>({} as TFilters);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Pagination and sorting
  const { pagination, sorting, handleSortingChange, onChangePage, onChangePageSize } = useEnhancedTable({
    initialSorting,
    onSearchQueryChange: searchQuery,
  });

  // Validation
  const { getTextFieldProps, validateItem, clearValidationErrors } = useTableValidation<TEntity>(
    createRequiredValidator<TEntity>(requiredFields as Array<keyof TEntity>)
  );

  // API hooks
  const { mutateAsync: upsertEntity } = useUpsertEntity();

  // Prepare request body
  const requestBody = useMemo(() => {
    if (prepareRequestBody) {
      return prepareRequestBody(filters, pagination, sorting);
    }

    let sortingDirection: string | undefined;
    if (sorting.length > 0) {
      sortingDirection = sorting[0].desc ? 'DESC' : 'ASC';
    }

    return {
      ...filters,
      paginationRequest: {
        page: pagination.pageIndex,
        size: pagination.pageSize,
        sortingColum: sorting.length > 0 ? sorting[0].id : undefined,
        sortingDirection,
      },
    };
  }, [filters, pagination, sorting, prepareRequestBody]);

  // Fetch data
  const { data: entityData, isLoading, isError, refetch } = useEntityList(requestBody);
  const { data: metadataData, refetch: refetchMetadata } = useEntityMetadata(filters);

  // Derived data
  const entities = useMemo(() => entityData?.data?.content || [], [entityData]);
  const entitiesTotal = useMemo(() => entityData?.data?.totalElements || 0, [entityData]);

  // Handle filter changes
  useEffect(() => {
    if (isInitialLoad) {
      setIsInitialLoad(false);
    } else {
      refetch();
    }
  }, [filters, refetch, isInitialLoad]);

  // Filter update function
  const updateFilter = useCallback(<K extends keyof TFilters>(key: K, value: TFilters[K]) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  }, []);

  // Table sorting handler
  const handleTableSort = useCallback(
    (columnId: string, direction: 'asc' | 'desc') => {
      handleSortingChange([{ id: columnId, desc: direction === 'desc' }]);
    },
    [handleSortingChange]
  );

  // Table filter handler
  const handleTableFilter = useCallback(
    (columnId: string, selectedValues: string[], allOptions?: { id: string; label: string; count: number }[]) => {
      const isAllSelected = allOptions && selectedValues.length === allOptions.length;

      if (!selectedValues.length || isAllSelected) {
        // This would need to be customized per entity type
        // For now, we'll provide a generic approach
        refetchMetadata();
        refetch();
        return;
      }

      // Refresh data after applying filters
      refetch();
    },
    [refetch, refetchMetadata]
  );

  // Entity save handler
  const handleEntitySave = useCallback(
    async ({
      values,
      row,
      exitEditingMode,
      exitCreatingMode,
    }: {
      values: Record<string, any>;
      row?: MRT_Row<TEntity>;
      exitEditingMode?: () => void;
      exitCreatingMode?: () => void;
    }): Promise<void> => {
      let entityData: TEntity;

      if (prepareEntityBody) {
        entityData = prepareEntityBody(values, row?.original);
      } else {
        entityData = { ...values, ...(row?.original?.id ? { id: row.original.id } : {}) } as TEntity;
      }

      // Validate the entity
      const isValid = customValidation ? customValidation(entityData) : validateItem(entityData);
      if (!isValid) {
        return;
      }

      const isEdit = Boolean(entityData.id);
      const operationType = isEdit ? 'update' : 'creation';

      try {
        await upsertEntity(entityData);

        openToast({
          title: `${entityName} ${isEdit ? 'updated' : 'created'} successfully`,
          toastType: 'success',
        });

        clearValidationErrors();
        refetch();

        if (exitEditingMode) exitEditingMode();
        if (exitCreatingMode) exitCreatingMode();
      } catch (error) {
        const errorMessage = error instanceof Error ? error : new Error(`${entityName} ${operationType} failed`);
        openToastError(errorMessage);
      }
    },
    [entityName, upsertEntity, validateItem, clearValidationErrors, refetch, prepareEntityBody, customValidation]
  );

  return {
    // State
    selectedEntityId,
    setSelectedEntityId,
    filters,
    setFilters,

    // Data
    entities,
    entitiesTotal,
    isLoading,
    isError,

    // Pagination & Sorting
    pagination,
    sorting,
    handleSortingChange,
    onChangePage,
    onChangePageSize,

    // Table operations
    handleEntitySave,

    // Validation
    getTextFieldProps,
    validateItem,
    clearValidationErrors,

    // Filtering
    updateFilter,
    handleTableSort,
    handleTableFilter,

    // Metadata
    metadataData,
    refetchMetadata,
    refetch,
  };
}
